import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import useAutoRefresh from "@/lib/useAutoRefresh";
import { ChatCircle, X, PaperPlaneTilt, Users, User } from "@phosphor-icons/react";
import {
  fetchChatConversations,
  fetchChatMessages,
  sendChatMessage,
  markChatRead,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const POLL_MS = 6000;
const CONV_POLL_MS = 15000;

function timeLabel(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();
    if (sameDay) {
      return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) + " " +
      d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function ChatPanel() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("general"); // "general" | "dm"
  const [activePeer, setActivePeer] = useState(null);
  const [conversations, setConversations] = useState({ general_unread: 0, peers: [], total_unread: 0 });
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const loadConversations = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetchChatConversations();
      setConversations(res);
    } catch {
      // silent
    }
  }, [user]);

  const loadMessages = useCallback(async () => {
    if (!user) return;
    try {
      if (tab === "general") {
        const res = await fetchChatMessages("general", null, 200);
        setMessages(res);
      } else if (tab === "dm" && activePeer) {
        const res = await fetchChatMessages("dm", activePeer.id, 200);
        setMessages(res);
      } else {
        setMessages([]);
      }
    } catch {
      // silent
    }
  }, [tab, activePeer, user]);

  // Poll conversations for unread count (paused when the tab is hidden)
  useEffect(() => {
    if (!user) return;
    loadConversations();
  }, [loadConversations, user]);
  useAutoRefresh(loadConversations, CONV_POLL_MS);

  // Poll messages for the active view
  const pollMessages = useCallback(() => { if (open) loadMessages(); }, [open, loadMessages]);
  useEffect(() => {
    if (!open || !user) return;
    loadMessages();
  }, [open, loadMessages, user]);
  useAutoRefresh(pollMessages, POLL_MS);

  // Auto-scroll only when the last message CHANGES (new message appended),
  // never on background polls that just refresh the same list.
  const lastMsgIdRef = useRef(null);
  useEffect(() => {
    const lastId = messages.length > 0 ? messages[messages.length - 1].id : null;
    if (lastId !== lastMsgIdRef.current) {
      lastMsgIdRef.current = lastId;
      if (listRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    }
  }, [messages]);

  // Reset scroll tracking when switching tab/peer
  useEffect(() => {
    lastMsgIdRef.current = null;
  }, [tab, activePeer]);

  // Mark as read whenever the view is open
  useEffect(() => {
    if (!open || !user) return;
    const doMark = async () => {
      try {
        if (tab === "general") {
          await markChatRead("general");
        } else if (activePeer) {
          await markChatRead("dm", activePeer.id);
        }
        loadConversations();
      } catch {
        // silent
      }
    };
    doMark();
  }, [open, tab, activePeer, messages.length, user, loadConversations]);

  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    if (tab === "dm" && !activePeer) {
      toast.error("Sélectionne un utilisateur");
      return;
    }
    setSending(true);
    try {
      const payload =
        tab === "general"
          ? { channel: "general", text }
          : { channel: "dm", recipient_id: activePeer.id, text };
      const saved = await sendChatMessage(payload);
      setMessages((prev) => [...prev, saved]);
      setDraft("");
      loadConversations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const totalUnread = conversations.total_unread || 0;

  if (!user) return null;

  return (
    <>
      {/* Floating toggle button — right */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed right-3 bottom-3 z-40 w-12 h-12 border-2 border-zinc-900 bg-white hover:bg-[var(--ny-gold)] hover:text-white shadow-lg flex items-center justify-center transition-colors"
        title="Discussion équipe"
        data-testid="chat-panel-toggle"
      >
        <ChatCircle size={22} weight="fill" />
        {totalUnread > 0 && (
          <span
            className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white"
            data-testid="chat-panel-badge"
          >
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {/* Panel — right slide */}
      <div
        className={`fixed right-0 top-0 bottom-0 z-50 w-[380px] max-w-[92vw] bg-white border-l-2 border-zinc-900 shadow-2xl flex flex-col transform transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid="chat-panel"
      >
        <div className="p-3 border-b-2 border-zinc-900 bg-zinc-900 text-white flex items-center gap-2">
          <ChatCircle size={18} weight="fill" className="text-[var(--ny-gold)]" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest font-bold">Discussion équipe</div>
            <div className="text-[10px] text-zinc-400 uppercase tracking-widest">
              {user.email}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-white hover:text-red-400"
            data-testid="chat-panel-close"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-300">
          <TabButton
            active={tab === "general"}
            onClick={() => {
              setTab("general");
              setActivePeer(null);
            }}
            icon={Users}
            label="Général"
            badge={conversations.general_unread}
            testId="chat-tab-general"
          />
          <TabButton
            active={tab === "dm"}
            onClick={() => setTab("dm")}
            icon={User}
            label="Direct"
            badge={(conversations.total_unread || 0) - (conversations.general_unread || 0)}
            testId="chat-tab-dm"
          />
        </div>

        {/* DM peer list */}
        {tab === "dm" && !activePeer && (
          <div className="flex-1 overflow-y-auto">
            {(conversations.peers || []).length === 0 && (
              <div className="p-6 text-center text-xs text-zinc-500">
                Aucun autre utilisateur.
              </div>
            )}
            {(conversations.peers || []).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setActivePeer(p)}
                className="w-full text-left px-3 py-2 border-b border-zinc-100 hover:bg-zinc-50 flex items-center gap-2"
                data-testid={`chat-peer-${p.id}`}
              >
                <div className="w-8 h-8 bg-zinc-900 text-white flex items-center justify-center font-bold text-xs uppercase">
                  {(p.name || "?").charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{p.name}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{p.email}</div>
                </div>
                {p.unread > 0 && (
                  <span className="ml-auto min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                    {p.unread}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Message list */}
        {(tab === "general" || activePeer) && (
          <>
            {tab === "dm" && activePeer && (
              <div className="px-3 py-1.5 border-b border-zinc-200 bg-zinc-50 flex items-center gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setActivePeer(null)}
                  className="text-zinc-500 hover:text-zinc-900 mono"
                  data-testid="chat-back-to-peers"
                >
                  ← Retour
                </button>
                <span className="font-bold">{activePeer.name}</span>
              </div>
            )}
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto p-2 space-y-1.5 bg-zinc-50"
              data-testid="chat-messages"
            >
              {messages.length === 0 && (
                <div className="text-center text-xs text-zinc-500 py-6">
                  Aucun message pour l&apos;instant.
                </div>
              )}
              {messages.map((m) => {
                const mine = m.sender_id === user.id;
                return (
                  <div
                    key={m.id}
                    className={`flex ${mine ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] px-2.5 py-1.5 text-sm border ${
                        mine
                          ? "bg-zinc-900 text-white border-zinc-900"
                          : "bg-white text-zinc-900 border-zinc-200"
                      }`}
                    >
                      {!mine && (
                        <div className="text-[10px] font-bold uppercase tracking-widest opacity-70 mb-0.5">
                          {m.sender_name}
                        </div>
                      )}
                      <div className="whitespace-pre-wrap break-words">{m.text}</div>
                      <div className={`text-[9px] mt-0.5 opacity-60 mono ${mine ? "text-right" : ""}`}>
                        {timeLabel(m.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-zinc-300 p-2 flex gap-1.5 bg-white">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                rows={2}
                placeholder={
                  tab === "general" ? "Message à toute l'équipe…" : `Message à ${activePeer?.name || ""}…`
                }
                className="flex-1 border border-zinc-300 px-2 py-1 text-sm rounded-none focus:outline-none focus:border-zinc-900 resize-none"
                data-testid="chat-input"
              />
              <button
                type="button"
                onClick={submit}
                disabled={sending || !draft.trim()}
                className="px-2 bg-zinc-900 text-white border border-zinc-900 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                data-testid="chat-send"
                title="Envoyer (Entrée)"
              >
                <PaperPlaneTilt size={16} weight="fill" />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function TabButton({ active, onClick, icon: Icon, label, badge, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 px-3 py-2 text-xs font-bold uppercase tracking-widest border-b-2 inline-flex items-center justify-center gap-1.5 ${
        active
          ? "border-[var(--ny-gold)] text-zinc-900 bg-white"
          : "border-transparent text-zinc-500 hover:text-zinc-900 bg-zinc-50"
      }`}
      data-testid={testId}
    >
      <Icon size={14} weight="bold" />
      {label}
      {badge > 0 && (
        <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[9px] flex items-center justify-center">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
}
